import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Printer, Eye, Trash2, CheckCircle, Download, Search, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Invoices = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = user?.role === 'admin';
  const username = user?.username?.toLowerCase() || '';
  
  // Only Arbe can edit/delete invoices
  const isArbe = username.includes('arbe');
  const canEditInvoices = isArbe;
  const canDeleteInvoices = isArbe;
  
  const [invoices, setInvoices] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedSubscriberId, setSelectedSubscriberId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/subscribers`)
      ]);
      setInvoices(invoicesRes.data);
      setSubscribers(subscribersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/invoices/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import invoices');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/invoices/template/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'invoices_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleGenerateForPayments = async () => {
    if (window.confirm('Generate invoices for all payments that do not have invoices yet?')) {
      try {
        const response = await axios.post(`${API}/invoices/generate-for-payments`);
        toast.success(response.data.message);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to generate invoices');
      }
    }
  };

  const handleCreateInvoice = async () => {
    if (!selectedSubscriberId) {
      toast.error('Please select a subscriber');
      return;
    }
    try {
      await axios.post(`${API}/invoices?subscriber_id=${selectedSubscriberId}`);
      toast.success('Invoice created successfully');
      setIsDialogOpen(false);
      setSelectedSubscriberId('');
      fetchData();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    }
  };

  const handlePrintInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDeleteInvoice = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await axios.delete(`${API}/invoices/${id}`);
        toast.success('Invoice deleted successfully');
        fetchData();
      } catch (error) {
        console.error('Error deleting invoice:', error);
        toast.error('Failed to delete invoice');
      }
    }
  };

  const handleMarkAsPaid = async (id) => {
    if (window.confirm('Mark this invoice as paid?')) {
      try {
        await axios.put(`${API}/invoices/${id}/mark-paid`);
        toast.success('Invoice marked as paid!');
        fetchData();
      } catch (error) {
        console.error('Error marking invoice as paid:', error);
        toast.error('Failed to update invoice');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Print View */}
      {selectedInvoice && (
        <div className="hidden print:block print-only">
          <div className="p-8 max-w-4xl mx-auto bg-white">
            <div className="border-b-2 border-slate-900 pb-6 mb-6">
              <h1 className="text-3xl font-jakarta font-bold text-slate-900">INVOICE</h1>
              <p className="text-slate-600 mt-1">WAQA Internet Services</p>
            </div>
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Invoice Details</h3>
                <p className="text-sm text-slate-600">Invoice #: {selectedInvoice.invoice_number}</p>
                <p className="text-sm text-slate-600">Issue Date: {new Date(selectedInvoice.issue_date).toLocaleDateString()}</p>
                <p className="text-sm text-slate-600">Due Date: {new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Bill To</h3>
                <p className="font-medium text-slate-900">{selectedInvoice.subscriber_name}</p>
                <p className="text-sm text-slate-600">{selectedInvoice.subscriber_mobile}</p>
                <p className="text-sm text-slate-600">{selectedInvoice.subscriber_address}</p>
              </div>
            </div>
            <div className="mb-8">
              <table className="w-full border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 border-b border-slate-200">Description</th>
                    <th className="text-right px-4 py-3 border-b border-slate-200">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3 border-b border-slate-200">Internet Subscription</td>
                    <td className="text-right px-4 py-3 border-b border-slate-200">₱{selectedInvoice.amount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-bold text-right">Total Amount Due</td>
                    <td className="text-right px-4 py-3 font-bold text-lg">₱{selectedInvoice.amount.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-sm text-slate-600">
              <p className="mb-2">Please make payment by the due date to avoid service interruption.</p>
              <p>Thank you for your business!</p>
            </div>
          </div>
        </div>
      )}

      {/* Screen View */}
      <div className="space-y-6 no-print" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 w-full sm:w-auto sm:max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <Input
                type="text"
                placeholder="Search by subscriber name..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
          {/* Hidden file input for import */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
          />
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            title="Download import template"
          >
            <FileSpreadsheet size={18} className="mr-2" />
            Template
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            <Upload size={18} className="mr-2" />
            {importing ? 'Importing...' : 'Import'}
          </Button>
          <Button 
            variant="outline" 
            onClick={async () => {
              setExporting(true);
              try {
                const response = await axios.get(`${API}/invoices/export/excel`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Invoices exported successfully!');
              } catch (error) {
                toast.error('Failed to export invoices');
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting || invoices.length === 0}
          >
            <Download size={20} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          {isAdmin && (
            <Button 
              variant="outline"
              onClick={handleGenerateForPayments}
              title="Generate invoices for payments without invoices"
              className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCircle size={18} className="mr-2" />
              Generate from Payments
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="create-invoice-btn">
                <Plus size={20} className="mr-2" />
                Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
              <DialogHeader>
                <DialogTitle>Create Invoice</DialogTitle>
                <DialogDescription>
                  Select a subscriber to generate an invoice for.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Select Subscriber</label>
                  <Select value={selectedSubscriberId} onValueChange={setSelectedSubscriberId}>
                    <SelectTrigger data-testid="invoice-subscriber-select">
                      <SelectValue placeholder="Select subscriber" />
                    </SelectTrigger>
                    <SelectContent>
                      {subscribers.map((subscriber) => (
                        <SelectItem key={subscriber.id} value={subscriber.id}>
                          {subscriber.name} - {subscriber.plan} (₱{subscriber.amount})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-btn">
                    Cancel
                  </Button>
                  <Button onClick={handleCreateInvoice} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-invoice-btn">
                    Create Invoice
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Invoices List */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="invoices-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Invoice #</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Subscriber</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Issue Date</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Due Date</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(() => {
                  const filteredInvoices = invoices.filter(invoice => 
                    invoice.subscriber_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
                  );
                  return filteredInvoices.length > 0 ? (
                    filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-slate-50 transition-colors" data-testid={`invoice-row-${invoice.id}`}>
                      <td className="px-6 py-4 font-mono text-sm text-slate-700">{invoice.invoice_number}</td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{invoice.subscriber_name}</p>
                        <p className="text-sm text-slate-500">{invoice.subscriber_mobile}</p>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-900">₱{invoice.amount.toLocaleString()}</td>
                      <td className="px-6 py-4 text-slate-700">{new Date(invoice.issue_date).toLocaleDateString()}</td>
                      <td className="px-6 py-4 text-slate-700">{new Date(invoice.due_date).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          invoice.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-amber-100 text-amber-700 border-amber-200'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          {canEditInvoices && invoice.status !== 'paid' && (
                            <button
                              onClick={() => handleMarkAsPaid(invoice.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Mark as paid"
                              data-testid={`mark-paid-${invoice.id}`}
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handlePrintInvoice(invoice)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Print invoice"
                            data-testid={`print-invoice-${invoice.id}`}
                          >
                            <Printer size={18} />
                          </button>
                          {canDeleteInvoices && (
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete invoice"
                              data-testid={`delete-invoice-${invoice.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      {searchTerm ? 'No invoices found matching your search' : 'No invoices created yet'}
                    </td>
                  </tr>
                );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoices;
